import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Clock, 
  Save, 
  Trash2, 
  Plus,
  CheckCircle,
  AlertCircle,
  GripVertical,
  X
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { id: 0, name: 'Monday', short: 'Mon' },
  { id: 1, name: 'Tuesday', short: 'Tue' },
  { id: 2, name: 'Wednesday', short: 'Wed' },
  { id: 3, name: 'Thursday', short: 'Thu' },
  { id: 4, name: 'Friday', short: 'Fri' },
  { id: 5, name: 'Saturday', short: 'Sat' },
  { id: 6, name: 'Sunday', short: 'Sun' }
];

// Generate time slots from 00:00 to 24:00 with 30-min intervals
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    slots.push(`${String(hour).padStart(2, '0')}:00`);
    slots.push(`${String(hour).padStart(2, '0')}:30`);
  }
  slots.push('24:00'); // End marker
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Convert time string to slot index
const timeToSlotIndex = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 2 + (minutes >= 30 ? 1 : 0);
};

// Convert slot index to time string
const slotIndexToTime = (index) => {
  const hour = Math.floor(index / 2);
  const minutes = (index % 2) * 30;
  return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Format time for display (12-hour format)
const formatTimeDisplay = (time) => {
  if (time === '24:00') return '12:00 AM';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
};

const TutorAvailabilityScheduler = ({ readOnly = false, tutorId = null }) => {
  const { getAccessToken, user } = useAuth();
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragDay, setDragDay] = useState(null);
  const [tutorName, setTutorName] = useState(null);
  const gridRef = useRef(null);

  // Fetch availability
  const fetchAvailability = useCallback(async () => {
    console.log('TutorAvailabilityScheduler: Fetching availability for tutorId:', tutorId);
    try {
      setLoading(true);
      setError(null);
      const token = getAccessToken();
      console.log('TutorAvailabilityScheduler: Token available:', !!token);
      if (!token) {
        console.log('TutorAvailabilityScheduler: No token, stopping');
        setLoading(false);
        return;
      }

      const endpoint = tutorId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/availability/${tutorId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/availability`;

      console.log('TutorAvailabilityScheduler: Fetching from:', endpoint);
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('TutorAvailabilityScheduler: Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('TutorAvailabilityScheduler: Data received:', data);
        setAvailability(data.slots || []);
        setTutorName(data.tutor_name);
      } else if (response.status === 404) {
        // No availability set yet
        console.log('TutorAvailabilityScheduler: No availability set (404)');
        setAvailability([]);
      } else {
        const err = await response.json();
        console.log('TutorAvailabilityScheduler: Error:', err);
        setError(err.detail || 'Failed to fetch availability');
      }
    } catch (err) {
      console.error('TutorAvailabilityScheduler: Fetch error:', err);
      setError('Failed to load availability');
    } finally {
      console.log('TutorAvailabilityScheduler: Setting loading to false');
      setLoading(false);
    }
  }, [getAccessToken, tutorId]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Save availability
  const saveAvailability = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const token = getAccessToken();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/availability`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            slots: availability.map(slot => ({
              day_of_week: slot.day_of_week,
              start_time: slot.start_time,
              end_time: slot.end_time
            }))
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailability(data.slots || []);
        setSuccess('Availability saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to save availability');
      }
    } catch (err) {
      console.error('Save availability error:', err);
      setError('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  // Check if a time slot conflicts with existing availability
  const hasConflict = (dayId, startTime, endTime, excludeIndex = -1) => {
    return availability.some((slot, index) => {
      if (slot.day_of_week !== dayId || index === excludeIndex) return false;
      const slotStart = timeToSlotIndex(slot.start_time);
      const slotEnd = timeToSlotIndex(slot.end_time);
      const newStart = timeToSlotIndex(startTime);
      const newEnd = timeToSlotIndex(endTime);
      return !(newEnd <= slotStart || newStart >= slotEnd);
    });
  };

  // Get slot at a specific position
  const getSlotAtPosition = (dayId, slotIndex) => {
    return availability.findIndex(slot => {
      if (slot.day_of_week !== dayId) return false;
      const start = timeToSlotIndex(slot.start_time);
      const end = timeToSlotIndex(slot.end_time);
      return slotIndex >= start && slotIndex < end;
    });
  };

  // Handle mouse down on time grid
  const handleMouseDown = (dayId, slotIndex) => {
    if (readOnly) return;
    
    // Check if clicking on an existing slot
    const existingSlotIndex = getSlotAtPosition(dayId, slotIndex);
    if (existingSlotIndex !== -1) {
      // Delete the slot
      setAvailability(prev => prev.filter((_, i) => i !== existingSlotIndex));
      return;
    }

    setIsDragging(true);
    setDragStart(slotIndex);
    setDragDay(dayId);
  };

  // Handle mouse move while dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || dragDay === null) return;
    // Mouse move is handled through onMouseEnter on cells
  }, [isDragging, dragDay]);

  // Handle mouse up to finish dragging
  const handleMouseUp = useCallback(() => {
    if (!isDragging || dragStart === null || dragDay === null) {
      setIsDragging(false);
      setDragStart(null);
      setDragDay(null);
      return;
    }

    // Get current mouse position from the last entered cell
    // For now, use the drag start if no movement
    const currentSlot = dragStart;
    const startSlot = Math.min(dragStart, currentSlot);
    const endSlot = Math.max(dragStart, currentSlot) + 1;

    const startTime = slotIndexToTime(startSlot);
    const endTime = slotIndexToTime(endSlot);

    // Only add if at least 30 min (1 slot)
    if (endSlot > startSlot && !hasConflict(dragDay, startTime, endTime)) {
      setAvailability(prev => [...prev, {
        day_of_week: dragDay,
        start_time: startTime,
        end_time: endTime
      }]);
    }

    setIsDragging(false);
    setDragStart(null);
    setDragDay(null);
  }, [isDragging, dragStart, dragDay]);

  // Track last entered slot for drag operations
  const [currentDragSlot, setCurrentDragSlot] = useState(null);

  const handleCellMouseEnter = (dayId, slotIndex) => {
    if (isDragging && dayId === dragDay) {
      setCurrentDragSlot(slotIndex);
    }
  };

  // Modified mouse up to use current drag slot
  const handleMouseUpWithSlot = useCallback(() => {
    if (!isDragging || dragStart === null || dragDay === null) {
      setIsDragging(false);
      setDragStart(null);
      setDragDay(null);
      setCurrentDragSlot(null);
      return;
    }

    const endSlotIndex = currentDragSlot !== null ? currentDragSlot : dragStart;
    const startSlot = Math.min(dragStart, endSlotIndex);
    const endSlot = Math.max(dragStart, endSlotIndex) + 1;

    const startTime = slotIndexToTime(startSlot);
    const endTime = slotIndexToTime(endSlot);

    if (endSlot > startSlot && !hasConflict(dragDay, startTime, endTime)) {
      setAvailability(prev => [...prev, {
        day_of_week: dragDay,
        start_time: startTime,
        end_time: endTime
      }]);
    }

    setIsDragging(false);
    setDragStart(null);
    setDragDay(null);
    setCurrentDragSlot(null);
  }, [isDragging, dragStart, dragDay, currentDragSlot]);

  // Global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUpWithSlot();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, handleMouseUpWithSlot]);

  // Add a quick slot via dropdown
  const addQuickSlot = (dayId, startTime, endTime) => {
    if (!hasConflict(dayId, startTime, endTime)) {
      setAvailability(prev => [...prev, {
        day_of_week: dayId,
        start_time: startTime,
        end_time: endTime
      }]);
    }
  };

  // Delete a specific slot
  const deleteSlot = (index) => {
    setAvailability(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all availability for a day
  const clearDay = (dayId) => {
    setAvailability(prev => prev.filter(slot => slot.day_of_week !== dayId));
  };

  // Clear all availability
  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all availability?')) {
      setAvailability([]);
    }
  };

  // Check if a cell should be highlighted during drag
  const isCellInDragRange = (dayId, slotIndex) => {
    if (!isDragging || dragDay !== dayId || dragStart === null) return false;
    const endSlot = currentDragSlot !== null ? currentDragSlot : dragStart;
    const start = Math.min(dragStart, endSlot);
    const end = Math.max(dragStart, endSlot);
    return slotIndex >= start && slotIndex <= end;
  };

  // Check if a cell is part of an existing availability slot
  const getCellAvailability = (dayId, slotIndex) => {
    return availability.find(slot => {
      if (slot.day_of_week !== dayId) return false;
      const start = timeToSlotIndex(slot.start_time);
      const end = timeToSlotIndex(slot.end_time);
      return slotIndex >= start && slotIndex < end;
    });
  };

  // Time labels to show (every 2 hours)
  const timeLabels = TIME_SLOTS.filter((_, i) => i % 4 === 0);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading availability...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-6 h-6 text-cyan-600" />
            {readOnly ? (
              tutorName ? `${tutorName}'s Availability` : 'Tutor Availability'
            ) : (
              'Set Your Weekly Availability'
            )}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {readOnly ? (
              'View available time slots for scheduling'
            ) : (
              'Click and drag to set your available hours. Click on a slot to remove it.'
            )}
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              disabled={availability.length === 0}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
            <Button
              onClick={saveAvailability}
              disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Availability
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-green-700 dark:text-green-400">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-6 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-cyan-500 rounded"></div>
          <span className="text-gray-600 dark:text-gray-400">Available</span>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-cyan-200 border-2 border-cyan-400 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Selecting...</span>
          </div>
        )}
      </div>

      {/* Timeline Grid */}
      <div className="overflow-x-auto" ref={gridRef}>
        <div className="min-w-[800px]">
          {/* Header row with day names */}
          <div className="grid grid-cols-8 gap-1 mb-2">
            <div className="w-16"></div> {/* Time label column */}
            {DAYS_OF_WEEK.map(day => (
              <div 
                key={day.id} 
                className="text-center font-semibold text-gray-700 dark:text-gray-300 py-2"
              >
                <span className="hidden sm:inline">{day.name}</span>
                <span className="sm:hidden">{day.short}</span>
              </div>
            ))}
          </div>

          {/* Time grid */}
          <div className="relative">
            {/* Time labels */}
            <div className="absolute left-0 top-0 w-16 flex flex-col">
              {timeLabels.map((time, i) => (
                <div 
                  key={time} 
                  className="text-xs text-gray-500 dark:text-gray-400 text-right pr-2"
                  style={{ height: '48px', lineHeight: '12px' }}
                >
                  {formatTimeDisplay(time)}
                </div>
              ))}
            </div>

            {/* Grid cells */}
            <div className="ml-16 grid grid-cols-7 gap-1">
              {DAYS_OF_WEEK.map(day => (
                <div key={day.id} className="flex flex-col">
                  {TIME_SLOTS.slice(0, -1).map((time, slotIndex) => {
                    const cellAvailability = getCellAvailability(day.id, slotIndex);
                    const isInDragRange = isCellInDragRange(day.id, slotIndex);
                    const isHourMark = slotIndex % 2 === 0;

                    return (
                      <div
                        key={`${day.id}-${slotIndex}`}
                        className={`
                          h-3 border-r border-gray-100 dark:border-gray-700 transition-colors cursor-pointer
                          ${isHourMark ? 'border-t border-gray-200 dark:border-gray-600' : ''}
                          ${cellAvailability ? 'bg-cyan-500 hover:bg-cyan-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
                          ${isInDragRange && !cellAvailability ? 'bg-cyan-200 dark:bg-cyan-800 border-cyan-400' : ''}
                          ${readOnly ? 'cursor-default' : ''}
                        `}
                        onMouseDown={() => !readOnly && handleMouseDown(day.id, slotIndex)}
                        onMouseEnter={() => !readOnly && handleCellMouseEnter(day.id, slotIndex)}
                        title={`${day.name} ${formatTimeDisplay(time)}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Slot Summary */}
      {availability.length > 0 && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Availability Summary
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {DAYS_OF_WEEK.map(day => {
              const daySlots = availability
                .filter(slot => slot.day_of_week === day.id)
                .sort((a, b) => a.start_time.localeCompare(b.start_time));

              if (daySlots.length === 0) return null;

              return (
                <div key={day.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 dark:text-white">{day.name}</span>
                    {!readOnly && (
                      <button
                        onClick={() => clearDay(day.id)}
                        className="text-red-500 hover:text-red-600 text-xs"
                        title={`Clear ${day.name}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {daySlots.map((slot, index) => {
                      const slotIndex = availability.findIndex(
                        s => s.day_of_week === slot.day_of_week && 
                             s.start_time === slot.start_time && 
                             s.end_time === slot.end_time
                      );
                      return (
                        <div 
                          key={index}
                          className="flex items-center justify-between text-sm bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 px-2 py-1 rounded"
                        >
                          <span>
                            {formatTimeDisplay(slot.start_time)} - {formatTimeDisplay(slot.end_time)}
                          </span>
                          {!readOnly && (
                            <button
                              onClick={() => deleteSlot(slotIndex)}
                              className="text-red-500 hover:text-red-600 ml-2"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Add Section - Only for editing mode */}
      {!readOnly && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Quick Add Common Hours
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Morning (9AM-12PM)', start: '09:00', end: '12:00' },
              { label: 'Afternoon (2PM-5PM)', start: '14:00', end: '17:00' },
              { label: 'Evening (6PM-9PM)', start: '18:00', end: '21:00' },
            ].map(preset => (
              <div key={preset.label} className="flex flex-wrap gap-1">
                {DAYS_OF_WEEK.slice(0, 5).map(day => ( // Mon-Fri
                  <Button
                    key={`${preset.label}-${day.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => addQuickSlot(day.id, preset.start, preset.end)}
                    disabled={hasConflict(day.id, preset.start, preset.end)}
                    className="text-xs"
                  >
                    {day.short} {preset.label.split(' ')[0]}
                  </Button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {availability.length === 0 && !loading && (
        <div className="mt-6 text-center py-8 text-gray-500 dark:text-gray-400">
          <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">No availability set</p>
          <p className="text-sm mt-1">
            {readOnly ? 
              'This tutor has not set their availability yet.' :
              'Click and drag on the timeline above to set your available hours.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TutorAvailabilityScheduler;
